import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import getLang from 'LearningCenterUI/utils/getLang';
import styled from 'styled-components';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIList from 'UIComponents/list/UIList';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
// @ts-expect-error untyped dependency
import FormattedDuration from 'I18n/components/FormattedDuration';
import ReducedLessonCard from '../../components/ContentCards/ReducedContentCard/subcomponents/ReducedLessonCard';
import UsageTracker from '../../utils/UsageTracker';
import { device } from '../../constants/StyledComponentConstants';
import { FILTER_TYPES } from '../../constants/TopFilterConstants';
import { CardMenuIcon } from '../../features/cardMenu';
import { useAvailableLanguagesByTrack, useFullTrack } from '../../features/tracks';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../hooks/searchParamHooks';
import CertificatePreview from '../../components/CertificatePreview';
import InstructorList from '../../components/InstructorList';
import { OverviewContent, OverviewModalCurriculumSection, OverviewModalDescriptionSection, useOverviewNavigation } from '../../features/overview';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ExpandableSection = styled.div.withConfig({
  displayName: "TrackOverviewModalContainer__ExpandableSection",
  componentId: "sc-1a2k9hm-0"
})(["display:flex;align-items:center;.start-lesson-button{margin-left:20px;flex-shrink:0;}@media screen and ", "{flex-direction:column;align-items:flex-start;.start-lesson-button{margin-top:8px;margin-left:0;width:fit-content;}}"], device.mobile);
const TrackOverviewModalContainer = ({
  trackId: trackIdFromProps,
  language: languageFromProps,
  allowFullWidthChildren
}) => {
  const {
    source,
    trackId: trackIdFromParams,
    language: paramLanguage
  } = useParams();
  const trackId = trackIdFromProps && trackIdFromProps.toString() || trackIdFromParams;
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const {
    goTo
  } = useOverviewNavigation();
  const {
    trackLanguagesLoading: availableLanguagesLoading,
    trackLanguages: availableLanguagesData
  } = useAvailableLanguagesByTrack(trackId);
  const language = useMemo(() => {
    const chosenLanguage = (languageFromProps || paramLanguage || searchParamLanguage || getLang()).toUpperCase();
    if (availableLanguagesData && availableLanguagesData.length && !availableLanguagesData.includes(chosenLanguage)) {
      // default to English if available
      return !availableLanguagesData.includes('EN') ? 'EN' : availableLanguagesData[0];
    }
    return chosenLanguage;
  }, [availableLanguagesData, languageFromProps, paramLanguage, searchParamLanguage]);
  const {
    trackLoading: loading,
    track,
    trackError: error
  } = useFullTrack(Number(trackId), language);
  const deprecated = track && track.deprecated;
  const deprecationCta = track && track.deprecationCta;
  const trackAggregatedLessonContents = useMemo(() => {
    return track && track.lessons.reduce((aggregated, lesson) => [...aggregated, ...lesson.content], []);
  }, [track]);
  const professors = useMemo(() => {
    if (!trackAggregatedLessonContents) return [];
    const professorsById = {};
    trackAggregatedLessonContents.forEach(asset => {
      if (asset.professors) {
        asset.professors.forEach(professor => {
          professorsById[professor.id] = professor;
        });
      }
    });
    return Object.values(professorsById);
  }, [trackAggregatedLessonContents]);
  const curriculumLessons = useMemo(() => {
    if (!track || !track.lessons.length) return [];
    return track.lessons.filter(lesson => !lesson.content.every(content => content.contentType === 'LINK'));
  }, [track]);
  const handleChangeSelectedLanguage = ({
    target: {
      value
    }
  }) => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-change-language',
      appearsIn: source,
      trackId: track ? track.id : null,
      name: track ? track.name : null,
      previousLanguage: language,
      newLanguage: value
    });
    goTo({
      id: parseInt(trackId, 10),
      type: 'TRACK',
      language: value
    });
  };
  const handleStartLessonClick = (lesson, index) => () => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-curriculum-click-start',
      contentType: 'LESSON',
      lessonId: lesson.id,
      lessonType: lesson.lessonType[0],
      appearsIn: 'course-overview-curriculum',
      position: index + 1,
      name: lesson.name,
      language
    });
  };
  const handleEnroll = () => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-enroll',
      appearsIn: source,
      contentLanguage: language,
      contentType: 'TRACK',
      lessonId: track ? track.id : null,
      name: track ? track.name : null
    });
  };
  const handleOrganisationUrlClick = (professorId, organisation, organisationUrl) => () => {
    UsageTracker.track('interaction', {
      action: 'professor-organisation-click',
      contentType: 'COURSE',
      appearsIn: 'course-overview-curriculum',
      organisation,
      organisationUrl,
      professorId,
      trackId
    });
  };
  return /*#__PURE__*/_jsxs(OverviewContent, {
    allowFullWidthChildren: allowFullWidthChildren,
    assetLanguage: language,
    deprecated: !!deprecated,
    deprecationCta: deprecationCta || undefined,
    error: error,
    fallbackThumbnailUrl: track && track.previewThumbnailRectangleUrl,
    headerIcons: /*#__PURE__*/_jsx(CardMenuIcon, {
      language: language,
      trackId: parseInt(trackId, 10),
      use: "on-dark"
    }),
    loading: loading || availableLanguagesLoading,
    trackId: trackId,
    children: [/*#__PURE__*/_jsx(OverviewModalDescriptionSection, {
      availableLanguages: availableLanguagesData,
      availableLanguagesLoading: availableLanguagesLoading,
      contentList: trackAggregatedLessonContents,
      description: track && track.description,
      duration: track && /*#__PURE__*/_jsxs(UIList, {
        inline: true,
        use: "inline-divided",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.overviewModal.numLessons",
          options: {
            number: track.lessons.length
          }
        }), /*#__PURE__*/_jsx(FormattedDuration, {
          from: new Date().getTime() - track.duration * 1000
        })]
      }),
      hasCertificate: track && !!track.certification,
      hasStudyGuide: track && !!track.studyGuide,
      id: track && track.id,
      onChangeSelectedLanguage: handleChangeSelectedLanguage,
      onEnroll: handleEnroll,
      selectedLanguage: language,
      title: track && track.name,
      type: "TRACK"
    }), track && track.certification && /*#__PURE__*/_jsx(CertificatePreview, {
      iconUrl: track.icon.location,
      title: track.name
    }), !!professors.length && /*#__PURE__*/_jsx(InstructorList, {
      handleOrganisationUrlClick: handleOrganisationUrlClick,
      language: language,
      professors: professors
    }), !!curriculumLessons.length && /*#__PURE__*/_jsx(OverviewModalCurriculumSection, {
      children: curriculumLessons.map((lesson, index) => /*#__PURE__*/_jsx(ReducedLessonCard, {
        appearsIn: "course-overview-curriculum",
        expandableContent: /*#__PURE__*/_jsxs(ExpandableSection, {
          className: "p-top-5",
          children: [/*#__PURE__*/_jsx(UIFlex, {
            children: lesson.description
          }), /*#__PURE__*/_jsx(UIRouterButton, {
            className: "start-lesson-button",
            "data-test-id": "start-lesson-button",
            onClick: handleStartLessonClick(lesson, index),
            role: "button",
            size: "small",
            to: `/tracks/${trackId}/${lesson.id}?language=${language}`,
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.overviewModal.viewLesson"
            })
          })]
        }),
        lessonId: lesson.id,
        position: index + 1,
        showContextMenu: false,
        showFavoriteTooltip: false,
        use: "on-dark",
        verticalGap: 28
      }, lesson.id))
    })]
  });
};
export default TrackOverviewModalContainer;